/* @flow */
import cx from 'classnames';
import { capitalize, get } from 'lodash';
import qs from 'qs';
import * as React from 'react';
import Modal from 'react-modal';
import Slider from 'react-slick';

import AdminStatisticsCommunicationChart from 'admin/components/AdminStatisticsCommunicationChart';
import AdminStatisticsReachChart from 'admin/components/AdminStatisticsReachChart';
import { formatData } from 'admin/lib/Chart';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Container from 'components/Container';
import Display from 'components/Display';
import Drift from 'components/Drift';
import Footer from 'components/Footer';
import Head from 'components/Head';
import Icon from 'components/Icon';
import Image from 'components/Image';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import EventTracking from 'lib/EventTracking';
import EngagementRatings from 'schools/constants/EngagementRatings';
import SchoolEngagementBadge from 'schools/components/SchoolEngagementBadge';
import SchoolEngagementBanner from 'schools/components/SchoolEngagementBanner';
import SchoolEngagementModal from 'schools/components/SchoolEngagementModal';
import { ADMIN_RECOMMENDATIONS, TEACHER_RECOMMENDATIONS } from 'schools/constants/SchoolViewCopy';

import SchoolViewChartFigure from './SchoolViewChartFigure';
import SchoolViewContainer from './SchoolViewContainer';
import type { School, Stats } from './SchoolViewDataContainer';
import SchoolViewHeading from './SchoolViewHeading';
import SchoolViewScreenshotFigures from './SchoolViewScreenshotFigures';
import SchoolViewSection from './SchoolViewSection';

import './SchoolView.scss';

const eventKey = 'school_engagement_rating';

const chartHeight = 320;

const utmParams = qs.stringify({
  utm_campaign: 'engagement-rating',
  utm_medium: 'site',
  utm_source: 'school-pages',
});

type Props = {
  school: School,
  stats?: ?Stats,
};

type State = {
  showRatingsModal: boolean,
};

export default class SchoolView extends React.Component<Props, State> {
  state: State = {
    showRatingsModal: false,
  };

  componentDidMount() {
    EventTracking.trackKey(`${eventKey}.view`);
  }

  handleCloseRatingsModal = () => {
    this.setState({ showRatingsModal: false });
  };

  handleShowRatingsModal = () => {
    this.setState({ showRatingsModal: true });
  };

  render() {
    const { school, stats } = this.props;
    const { showRatingsModal } = this.state;

    const {
      city,
      great_schools_url: greatSchoolsUrl,
      messaging_users_count: activeUsersCount,
      next_rating: ratingNext,
      org_name: name,
      pct_schools_engaged_rating: schoolsEngagedPercent,
      pct_schools_next_rating: schoolsNextRatingPercent,
      pct_schools_same_rating: similarSchoolsPercent,
      pct_similar_schools_active: schoolsActivePercent,
      pct_similar_schools_emerging: schoolsEmergingPercent,
      pct_similar_schools_growing: schoolsGrowingPercent,
      school_pages_equivalent_grade: gradeLevel,
      state,
      url,
    } = school;
    let { school_pages_rating: rating } = school;

    // some schools set to `no rating`
    if (!EngagementRatings.RATINGS.includes(rating)) {
      rating = undefined;
    }

    const showComparativeStats =
      name &&
      state &&
      schoolsActivePercent &&
      schoolsEmergingPercent &&
      schoolsEngagedPercent &&
      schoolsGrowingPercent;

    const contentHeaderContent = (
      <React.Fragment>
        {city && state && (
          <Row size="xxsmall">
            <Text size="small">
              {city}, {state}
            </Text>
          </Row>
        )}
        {name && <SchoolViewHeading element="h1">{name}</SchoolViewHeading>}
        <Row size="small">
          <Text element="h2" size="medium">
            {rating ? (
              <React.Fragment>
                Engagement rating: <Text weight="semibold">{capitalize(rating)}</Text>
              </React.Fragment>
            ) : (
              'An engagement rating isn’t available for this school.'
            )}
          </Text>
        </Row>
        <Link
          weight="semibold"
          className="SchoolView-modalLink"
          onClick={this.handleShowRatingsModal}
          eventKey={`${eventKey}.content_header.ratings_modal_cta.click`}>
          What does this mean?
        </Link>
      </React.Fragment>
    );

    const cta = (
      <Button
        to={`${LinkConstants.ADMIN_CONTACT}?${utmParams}&utm_content=callout`}
        eventKey={`${eventKey}.content_footer.cta.click`}>
        Get started
      </Button>
    );

    return (
      <div className="SchoolView">
        <Head
          title={name || null}
          description="See and understand engagement across your entire school with the Remind
            engagement rating, exclusive community engagement statistics, and educator-tested
            strategies for activating engagement in your organization."
          image={rating ? EngagementRatings.BADGES[rating].og : null}
          twitterCard
        />
        <header className="SchoolView-header">
          <div className="SchoolView-header-inner">
            <Link
              href={LinkConstants.MARKETING}
              target="_blank"
              className="SchoolView-header-mascot"
              eventKey={`${eventKey}.header.brand.click`}>
              <Image src={require('assets/images/schools/SchoolView/logo.svg')} alt="Remind" />
            </Link>
            <Link
              href={LinkConstants.ENGAGEMENT_RATINGS}
              weight="semibold"
              eventKey={`${eventKey}.header.cta.click`}>
              <Display hide="480">Learn about the engagement rating</Display>
              <Display show="480">Learn about the rating</Display>
            </Link>
          </div>
        </header>
        <SchoolViewSection
          theme="dark"
          className={cx(
            'SchoolView-contentHeader',
            !rating && 'SchoolView-contentHeader--noRating'
          )}>
          <SchoolViewContainer className="SchoolView-contentHeader-container">
            {rating ? (
              <MediaObject center gutter="xxxxlarge" className="SchoolView-schoolBrand">
                {rating && (
                  <SchoolEngagementBadge rating={rating} onClick={this.handleShowRatingsModal} />
                )}
                <div>{contentHeaderContent}</div>
              </MediaObject>
            ) : (
              contentHeaderContent
            )}
          </SchoolViewContainer>
        </SchoolViewSection>
        <SchoolViewSection>
          {showComparativeStats && (
            <Container>
              <Box className="SchoolView-box">
                <BoxBody>
                  <div className="SchoolView-box-inner">
                    <Text align="left" element="section" className="SchoolView-box-inner-body">
                      {rating ? (
                        <SchoolViewHeading>
                          {rating === 'engaged' && similarSchoolsPercent && (
                            <React.Fragment>
                              Congratulations! {similarSchoolsPercent}% of similar schools in{' '}
                              {state} are also rated Engaged.
                            </React.Fragment>
                          )}
                          {rating !== 'engaged' && ratingNext && schoolsNextRatingPercent && (
                            <React.Fragment>
                              Get to the next level: {schoolsNextRatingPercent}% of similar schools
                              in {state} are rated {capitalize(ratingNext)}.
                            </React.Fragment>
                          )}
                        </SchoolViewHeading>
                      ) : (
                        <SchoolViewHeading>
                          How similar schools are rated in {state}
                        </SchoolViewHeading>
                      )}
                      <Text element="p" size="medium">
                        {rating && name ? (
                          <React.Fragment>
                            {rating === 'engaged' && (
                              <React.Fragment>
                                Learn how you can build on the engagement you’ve achieved to target
                                specific needs and outcomes at {name}.
                              </React.Fragment>
                            )}
                            {rating !== 'engaged' && (
                              <React.Fragment>
                                Learn how you can use Remind to bring consistent, widespread, and
                                meaningful engagement to every level at {name}.
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        ) : (
                          'Learn how you can improve the reach, quality, and cadence of communication across your organization.'
                        )}
                      </Text>
                      {cta}
                    </Text>
                    <Text
                      align="center"
                      element="div"
                      size="small"
                      className="SchoolView-box-inner-figure">
                      <Row size="small">
                        <div className="SchoolView-chart">
                          {EngagementRatings.RATINGS.map(item => {
                            let percentage = schoolsEmergingPercent;

                            if (item === 'growing') {
                              percentage = schoolsGrowingPercent;
                            } else if (item === 'active') {
                              percentage = schoolsActivePercent;
                            } else if (item === 'engaged') {
                              percentage = schoolsEngagedPercent;
                            }

                            return (
                              <div key={item} className="SchoolView-barWrap">
                                <div className="SchoolView-bar">
                                  <div className="SchoolView-bar-spacer" />
                                  {percentage && (
                                    <div
                                      style={{ height: `${percentage}%` }}
                                      className={cx(
                                        'SchoolView-bar-amount',
                                        `SchoolView-bar-amount--${item}`
                                      )}>
                                      <Text
                                        weight="semibold"
                                        className={cx(
                                          'SchoolView-bar-label',
                                          `SchoolView-bar-label--${item}`
                                        )}>
                                        {percentage}%
                                      </Text>
                                    </div>
                                  )}
                                </div>
                                <Image src={EngagementRatings.BADGES[item].svg} width={42} />
                              </div>
                            );
                          })}
                        </div>
                      </Row>
                      Similar schools in {state}
                    </Text>
                  </div>
                </BoxBody>
              </Box>
            </Container>
          )}
          {rating && stats && (
            <Row size="xxxxxlarge">
              <SchoolViewContainer>
                <SchoolViewHeading align="center" cta={!showComparativeStats && cta}>
                  {activeUsersCount && name ? (
                    <React.Fragment>
                      <span className="SchoolView--highlight">
                        {activeUsersCount.toLocaleString()} teachers, students, and parents
                      </span>{' '}
                      at {name} are actively communicating on Remind.
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      Measure <span className="SchoolView--highlight">community engagement</span> at
                      your school with Remind.
                    </React.Fragment>
                  )}
                </SchoolViewHeading>
              </SchoolViewContainer>
              <div>
                <Slider
                  dots
                  nextArrow={
                    <a href="#">
                      <Icon name="chevron-right" aria-label="Go forward" />
                    </a>
                  }
                  prevArrow={
                    <a href="#">
                      <Icon name="chevron-left" aria-label="Go back" />
                    </a>
                  }>
                  <div className="SchoolView-slide">
                    <Container>
                      <SchoolViewChartFigure
                        title={
                          get(stats, 'communication_usage.chart')
                            ? `How many messages were delivered at ${name} in the last 6 months?`
                            : `See how many messages were delivered at ${name} in the last 6 months`
                        }
                        chartType="communication"
                        copy="Most engagement in a school community occurs at the classroom level.
                    This includes class updates and two-way communication between teachers and
                    families.">
                        {get(stats, 'communication_usage.chart') ? (
                          <AdminStatisticsCommunicationChart
                            announcementsColor={EngagementRatings.COLORS[rating].light}
                            conversationsColor={EngagementRatings.COLORS[rating].dark}
                            data={formatData(stats.communication_usage.chart, 'month')}
                            height={chartHeight}
                            simple
                            tooltip
                          />
                        ) : (
                          <Image
                            src={require('assets/images/schools/SchoolView/charts/communication-chart---null.png')}
                            srcSet={`${require('assets/images/schools/SchoolView/charts/communication-chart---null@2x.png')} 2x`}
                            full
                          />
                        )}
                      </SchoolViewChartFigure>
                    </Container>
                  </div>
                  <div className="SchoolView-slide">
                    <Container>
                      <SchoolViewChartFigure
                        title={
                          get(stats, 'reach_usage.chart')
                            ? `How many students and parents at ${name} received messages in the last 6 months?`
                            : `See how many students and parents at ${name} received messages in the last 6 months`
                        }
                        chartType="reach"
                        copy="The reach of a school community is determined by access to effective
                    communication channels and how frequently teachers communicate with students
                    and parents.">
                        {get(stats, 'reach_usage.chart') ? (
                          <AdminStatisticsReachChart
                            data={formatData(stats.reach_usage.chart, 'month')}
                            height={chartHeight}
                            parentsColor={EngagementRatings.COLORS[rating].dark}
                            simple
                            students={gradeLevel === 'high_school'}
                            studentsColor={EngagementRatings.COLORS[rating].light}
                            teachers={false}
                            tooltip
                          />
                        ) : (
                          <Image
                            src={require('assets/images/schools/SchoolView/charts/reach-chart---null.png')}
                            srcSet={`${require('assets/images/schools/SchoolView/charts/reach-chart---null@2x.png')} 2x`}
                            full
                          />
                        )}
                      </SchoolViewChartFigure>
                    </Container>
                  </div>
                </Slider>
              </div>
            </Row>
          )}
          <Container>
            <Text align="center" element="div">
              <Row size="xxxlarge">
                <header>
                  <SchoolViewContainer>
                    <SchoolViewHeading
                      flush
                      subtext={
                        rating &&
                        similarSchoolsPercent &&
                        state && (
                          <React.Fragment>
                            {rating === 'engaged' && (
                              <React.Fragment>
                                {similarSchoolsPercent}% of similar schools in {state} are rated{' '}
                                <strong>{capitalize(rating)}</strong>.
                              </React.Fragment>
                            )}
                            {rating !== 'engaged' && schoolsEngagedPercent && (
                              <React.Fragment>
                                {similarSchoolsPercent}% of similar schools in {state} are rated{' '}
                                <strong>{capitalize(rating)}</strong>, and {schoolsEngagedPercent}%
                                are rated <strong>Engaged</strong>.
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )
                      }>
                      Improve engagement at the classroom level.
                    </SchoolViewHeading>
                  </SchoolViewContainer>
                </header>
              </Row>
              <Row size="xxxxxlarge">
                <SchoolViewScreenshotFigures
                  items={[
                    {
                      alt: 'Summary of deliveries of messages on Remind',
                      caption: 'Establish effective communication with mobile-first notifications.',
                      image: require('assets/images/schools/SchoolView/screenshots/delivery-summary.png'),
                      image2x: require('assets/images/schools/SchoolView/screenshots/delivery-summary@2x.png'),
                    },
                    {
                      alt: 'An example of a message on Remind',
                      caption:
                        'Encourage two-way communication with education-specific safeguards.',
                      image: require('assets/images/schools/SchoolView/screenshots/chat.png'),
                      image2x: require('assets/images/schools/SchoolView/screenshots/chat@2x.png'),
                    },
                  ]}
                />
              </Row>
              <Row>
                <SchoolViewContainer size="small">
                  <SchoolEngagementBanner
                    badgeAction={this.handleShowRatingsModal}
                    cta="Learn more about Remind accounts for teachers"
                    rating={rating}
                    to={`${LinkConstants.TEACHERS}?${utmParams}&utm_content=tips`}
                    eventKey={`${eventKey}.teachers.cta.click`}>
                    {rating
                      ? TEACHER_RECOMMENDATIONS[rating]
                      : 'Create class communication channels that reach students and parents on the devices they actually use: their phones.'}
                  </SchoolEngagementBanner>
                </SchoolViewContainer>
              </Row>
            </Text>
          </Container>
        </SchoolViewSection>
        <SchoolViewSection theme="dark">
          <Container>
            <Text align="center" element="div">
              <Row size="xxxlarge">
                <header>
                  <Container size="small">
                    <SchoolViewHeading
                      flush
                      subtext={
                        <React.Fragment>
                          62% of Remind-rostered schools are rated <strong>Engaged</strong>.
                        </React.Fragment>
                      }>
                      Activate engagement across your entire school community.
                    </SchoolViewHeading>
                  </Container>
                </header>
              </Row>
              <Row size="xxxxxlarge">
                <SchoolViewScreenshotFigures
                  items={[
                    {
                      alt: 'An example of a list of schools on Remind',
                      caption: 'Consolidate new and existing engagement on a single platform.',
                      image: require('assets/images/schools/SchoolView/screenshots/rostering.png'),
                      image2x: require('assets/images/schools/SchoolView/screenshots/rostering@2x.png'),
                    },
                    {
                      alt: 'Remind’s voice calling feature',
                      caption:
                        'Leverage effective communication channels for school notifications.',
                      image: require('assets/images/schools/SchoolView/screenshots/advanced-messaging.png'),
                      image2x: require('assets/images/schools/SchoolView/screenshots/advanced-messaging@2x.png'),
                    },
                  ]}
                />
              </Row>
              <Row>
                <SchoolViewContainer size="small">
                  <SchoolEngagementBanner
                    badgeAction={this.handleShowRatingsModal}
                    cta="Learn more about Remind Hub for schools and districts"
                    rating={rating}
                    theme="dark"
                    to={`${LinkConstants.ADMIN_CONTACT}?${utmParams}&utm_content=tips`}
                    eventKey={`${eventKey}.admins.cta.click`}>
                    {rating
                      ? ADMIN_RECOMMENDATIONS[rating]
                      : 'Improve engagement at scale with a platform as easy to use for administrators and staff members as it is for students and parents.'}
                  </SchoolEngagementBanner>
                </SchoolViewContainer>
              </Row>
            </Text>
          </Container>
        </SchoolViewSection>

        <SchoolViewSection angle={false} className="SchoolView-contentFooter">
          {greatSchoolsUrl ? (
            <Container size="medium">
              <MediaObject center>
                <Image
                  src={require('assets/images/schools/SchoolView/school.png')}
                  srcSet={`${require('assets/images/schools/SchoolView/school@2x.png')} 2x`}
                />
                <div>
                  <Row size="small">
                    {name && (
                      <Text element="h2" font="serif" size="xxxlarge">
                        {name}
                      </Text>
                    )}
                    {city && state && (
                      <Text element="div" size="large" weight="semibold">
                        {city}, {state}
                      </Text>
                    )}
                    {url && (
                      <Link
                        href={url}
                        target="_blank"
                        eventKey={`${eventKey}.content_footer.organization_url.click`}>
                        <Text element="div" size="medium" weight="semibold">
                          {url}
                        </Text>
                      </Link>
                    )}
                  </Row>
                  <Button
                    href={`${greatSchoolsUrl}?${utmParams}&utm_content=footer`}
                    size="medium"
                    target="_blank"
                    eventKey={`${eventKey}.content_footer.cta.click`}>
                    See GreatSchools profile
                  </Button>
                </div>
              </MediaObject>
            </Container>
          ) : (
            <Text align="center" element="div">
              <Container size="small">
                <Row>
                  <Text element="h2" font="serif" size="xxxlarge">
                    <span className="SchoolView--highlight">Effective engagement</span> improves
                    student outcomes.
                  </Text>
                </Row>
                <Button
                  href={
                    rating ? undefined : `${LinkConstants.PLANS}?${utmParams}&utm_content=footer`
                  }
                  to={
                    rating
                      ? `${LinkConstants.ADMIN_CONTACT}?${utmParams}&utm_content=footer`
                      : undefined
                  }
                  eventKey={`${eventKey}.content_footer.cta.click`}>
                  Get started
                </Button>
              </Container>
            </Text>
          )}
        </SchoolViewSection>
        <Footer
          disclaimer={`This ${
            rating ? 'rating' : 'page'
          } is not an endorsement of Remind by ${name}.`}
        />
        <Modal isOpen={showRatingsModal} className="SchoolView-ratingsModal">
          <SchoolEngagementModal
            eventKey={eventKey}
            rating={rating}
            utmParams={utmParams}
            onRequestHide={this.handleCloseRatingsModal}
          />
        </Modal>
        <Drift />
      </div>
    );
  }
}
